import React, { useState, useRef } from 'react';
import { X, User, Bell, Palette, Clock, Download, Shield, Moon, Sun, Upload, RotateCcw, AlertTriangle, Check, Volume2, VolumeX, Smartphone, Edit, TestTube } from 'lucide-react';
import { usePreferencesStore, useSettingsStore, useStoreActions } from '@/store/useStore';
import { EditProfileModal } from './EditProfileModal';
import { useClassReminders } from '@/hooks/useClassReminders';
import analytics from '@/utils/analytics';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userPreferences: any;
}

// ─── Dark design tokens (match the dashboard) ─────────────────────────────────
const T = {
  panel: '#121212',
  surface: 'rgba(255,255,255,.03)',
  border: 'rgba(255,255,255,.07)',
  borderHov: 'rgba(255,255,255,.13)',
  teal: '#14B8A6',
  tealBg: 'rgba(20,184,166,.1)',
  tealBorder: 'rgba(20,184,166,.22)',
  text: '#fff',
  text60: 'rgba(255,255,255,.6)',
  text40: 'rgba(255,255,255,.4)',
  text25: 'rgba(255,255,255,.25)',
  mono: "'JetBrains Mono',monospace",
  serif: "'Instrument Serif',serif",
  sans: "'Outfit',sans-serif",
};

export function SettingsModal({ isOpen, onClose, userPreferences }: SettingsModalProps) {
  const { theme, toggleTheme } = usePreferencesStore();
  const { notifications, preferences, privacy, updateNotifications, updatePreferences, updatePrivacy, resetToDefaults, exportSettings, importSettings } = useSettingsStore();
  const { clearSearchHistory, clearFavorites } = useStoreActions();
  const { sendTestReminder, areRemindersEnabled } = useClassReminders();

  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showClearDataConfirm, setShowClearDataConfirm] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [importStatus, setImportStatus] = useState<'none' | 'success' | 'error'>('none');
  const [notificationStatus, setNotificationStatus] = useState<'none' | 'requesting' | 'granted' | 'denied'>('none');
  const [operationStatus, setOperationStatus] = useState<'none' | 'success' | 'error'>('none');
  const [testReminderStatus, setTestReminderStatus] = useState<'none' | 'success' | 'error'>('none');
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleThemeChange = (newTheme: 'light' | 'dark') => {
    if (newTheme !== theme) {
      toggleTheme();
      analytics.changeTheme(newTheme);
    }
  };

  const handleNotificationChange = (key: keyof typeof notifications, value: boolean | number) => {
    updateNotifications({ [key]: value });
    if (key === 'pushNotifications' && value === true) analytics.enableNotifications();
    else if (key === 'reminderTime' && typeof value === 'number') analytics.setReminderTime(value);
  };

  const handlePreferenceChange = (key: keyof typeof preferences, value: any) => {
    updatePreferences({ [key]: value });
  };

  const handlePrivacyChange = (key: keyof typeof privacy, value: boolean) => {
    updatePrivacy({ [key]: value });
  };

  const handleExportSettings = () => {
    try {
      const settingsData = exportSettings();
      const blob = new Blob([settingsData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `settings-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setImportStatus('success');
      setTimeout(() => setImportStatus('none'), 3000);
    } catch (error) {
      setImportStatus('error');
      setTimeout(() => setImportStatus('none'), 3000);
    }
  };

  const handleImportSettings = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !file.name.toLowerCase().endsWith('.json') || file.size > 1024 * 1024) {
      setImportStatus('error');
      setTimeout(() => setImportStatus('none'), 3000);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const success = importSettings(e.target?.result as string);
        setImportStatus(success ? 'success' : 'error');
        if (fileInputRef.current) fileInputRef.current.value = '';
      } catch { setImportStatus('error'); }
      setTimeout(() => setImportStatus('none'), 3000);
    };
    reader.readAsText(file);
  };

  const handleResetSettings = () => {
    resetToDefaults();
    setShowResetConfirm(false);
    setOperationStatus('success');
    setTimeout(() => setOperationStatus('none'), 3000);
  };

  const handleClearAllData = () => {
    clearSearchHistory();
    clearFavorites();
    localStorage.removeItem('recentGlobalSearches');
    localStorage.removeItem('recentTimetableSearches');
    resetToDefaults();
    setShowClearDataConfirm(false);
    setOperationStatus('success');
    setTimeout(() => setOperationStatus('none'), 3000);
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      setNotificationStatus('requesting');
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        updateNotifications({ pushNotifications: true });
        setNotificationStatus('granted');
        new Notification('University Timetable', { body: 'Notifications enabled!', icon: '/icons/android-chrome-192x192.png' });
      } else {
        updateNotifications({ pushNotifications: false });
        setNotificationStatus('denied');
      }
      setTimeout(() => setNotificationStatus('none'), 3000);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      style={{ background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <style>{`
        .set-row{display:flex;align-items:center;justify-content:space-between;gap:12px}
        .set-seg{padding:7px 14px;border-radius:8px;font-family:${T.mono};font-size:11px;letter-spacing:.04em;cursor:pointer;transition:.15s;border:1px solid transparent;background:transparent;color:${T.text40};display:flex;align-items:center;justify-content:center;gap:6px}
        .set-seg.on{background:${T.tealBg};border-color:${T.tealBorder};color:${T.teal}}
        .set-seg.off:hover{background:rgba(255,255,255,.04);color:${T.text60}}
        .set-btn{display:flex;align-items:center;justify-content:center;gap:8px;padding:11px;border-radius:10px;font-family:${T.sans};font-size:12px;font-weight:500;cursor:pointer;background:${T.surface};border:1px solid ${T.border};color:${T.text};transition:.15s}
        .set-btn:hover{background:rgba(255,255,255,.055);border-color:${T.borderHov}}
        .set-x:hover{background:rgba(255,255,255,.08)!important;color:#fff!important}
        .set-select{background:#1a1a1a;color:#fff;border:1px solid ${T.border};border-radius:8px;padding:6px 10px;font-family:${T.mono};font-size:11px;outline:none;cursor:pointer}
      `}</style>

      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:w-[95vw] sm:max-w-2xl max-h-[92vh] sm:max-h-[86vh] rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col"
        style={{ background: T.panel, border: `1px solid ${T.border}`, boxShadow: '0 24px 70px rgba(0,0,0,.6)', fontFamily: T.sans, color: T.text, paddingBottom: 'env(safe-area-inset-bottom,0px)' }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: T.tealBg, border: `1px solid ${T.tealBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.teal }}>
              <Palette className="w-4 h-4" />
            </div>
            <h2 style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 400, letterSpacing: '-.01em' }}>Settings</h2>
          </div>
          <button onClick={onClose} className="set-x" style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,.05)', border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.text40, cursor: 'pointer', transition: '.15s' }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Status */}
        {(importStatus !== 'none' || operationStatus !== 'none') && (
          <div style={{ padding: '12px 20px 0' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, fontSize: 12,
              background: (importStatus === 'success' || operationStatus === 'success') ? T.tealBg : 'rgba(239,68,68,.1)',
              border: `1px solid ${(importStatus === 'success' || operationStatus === 'success') ? T.tealBorder : 'rgba(239,68,68,.25)'}`,
              color: (importStatus === 'success' || operationStatus === 'success') ? T.teal : '#EF4444',
            }}>
              {(importStatus === 'success' || operationStatus === 'success') ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
              {importStatus === 'success' ? 'Settings saved!' : importStatus === 'error' ? 'Import failed' : operationStatus === 'success' ? 'Done!' : 'Failed'}
            </div>
          </div>
        )}

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Profile */}
          <Section icon={User} title="Profile">
            <div className="grid grid-cols-2 gap-x-3 gap-y-3" style={{ marginBottom: 14 }}>
              {[
                { k: 'Name', v: userPreferences?.name || 'Student' },
                { k: 'Program', v: userPreferences?.program },
                { k: 'Department', v: userPreferences?.department },
                { k: 'Semester', v: userPreferences?.semester },
              ].map(({ k, v }) => (
                <div key={k}>
                  <div style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.12em', color: T.text25 }}>{String(k).toUpperCase()}</div>
                  <div style={{ fontSize: 13, color: T.text, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v || '—'}</div>
                </div>
              ))}
            </div>
            <button onClick={() => setShowEditProfile(true)} className="set-btn" style={{ width: '100%', color: T.teal, borderColor: T.tealBorder, background: T.tealBg }}>
              <Edit className="w-4 h-4" /> Edit Profile
            </button>
          </Section>

          {/* Appearance */}
          <Section icon={Palette} title="Appearance">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <div style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.12em', color: T.text25, marginBottom: 8 }}>THEME</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className={`set-seg ${theme === 'light' ? 'on' : 'off'}`} style={{ flex: 1 }} onClick={() => handleThemeChange('light')}><Sun className="w-4 h-4" /> Light</button>
                  <button className={`set-seg ${theme === 'dark' ? 'on' : 'off'}`} style={{ flex: 1 }} onClick={() => handleThemeChange('dark')}><Moon className="w-4 h-4" /> Dark</button>
                </div>
              </div>
              <Row label="Compact Mode"><Toggle checked={preferences.compactMode} onChange={(c) => handlePreferenceChange('compactMode', c)} /></Row>
              <Row label="Show Weekends"><Toggle checked={preferences.showWeekends} onChange={(c) => handlePreferenceChange('showWeekends', c)} /></Row>
            </div>
          </Section>

          {/* Notifications */}
          <Section
            icon={Bell}
            title="Notifications"
            action={!notifications.pushNotifications && (
              <button onClick={requestNotificationPermission} disabled={notificationStatus === 'requesting'} className="set-seg on" style={{ padding: '5px 11px' }}>
                <Smartphone className="w-3 h-3" /> ENABLE
              </button>
            )}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Row label="Class Reminders" icon={Bell}><Toggle checked={notifications.classReminders} onChange={(c) => handleNotificationChange('classReminders', c)} /></Row>
              {notifications.classReminders && (
                <div style={{ marginLeft: 0, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 12, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: T.text60 }}>Remind</span>
                  <select value={notifications.reminderTime} onChange={(e) => handleNotificationChange('reminderTime', parseInt(e.target.value))} className="set-select">
                    <option value={5}>5 min</option><option value={10}>10 min</option><option value={15}>15 min</option><option value={30}>30 min</option>
                  </select>
                  <span style={{ fontSize: 12, color: T.text60 }}>before</span>
                  {areRemindersEnabled() && (
                    <button onClick={async () => {
                      try { const success = await sendTestReminder(); setTestReminderStatus(success ? 'success' : 'error'); }
                      catch { setTestReminderStatus('error'); }
                      setTimeout(() => setTestReminderStatus('none'), 3000);
                    }} className="set-seg off" style={{ marginLeft: 'auto', border: `1px solid ${T.border}` }}>
                      <TestTube className="w-3 h-3" /> Test {testReminderStatus === 'success' && '✓'}
                    </button>
                  )}
                </div>
              )}
              <Row label="Schedule Changes" icon={AlertTriangle}><Toggle checked={notifications.scheduleChanges} onChange={(c) => handleNotificationChange('scheduleChanges', c)} /></Row>
              <Row label="Sound" icon={notifications.soundEnabled ? Volume2 : VolumeX}><Toggle checked={notifications.soundEnabled} onChange={(c) => handleNotificationChange('soundEnabled', c)} /></Row>
            </div>
          </Section>

          {/* Preferences */}
          <Section icon={Clock} title="Preferences">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Row label="Show Full Timetable on Dashboard"><Toggle checked={preferences.showFullTimetableOnDashboard} onChange={(c) => handlePreferenceChange('showFullTimetableOnDashboard', c)} /></Row>
              <Row label="Time Format">
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className={`set-seg ${preferences.timeFormat === '12h' ? 'on' : 'off'}`} onClick={() => handlePreferenceChange('timeFormat', '12h')}>12h</button>
                  <button className={`set-seg ${preferences.timeFormat === '24h' ? 'on' : 'off'}`} onClick={() => handlePreferenceChange('timeFormat', '24h')}>24h</button>
                </div>
              </Row>
              <Row label="Week Starts On">
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className={`set-seg ${preferences.weekStartsOn === 'Sunday' ? 'on' : 'off'}`} onClick={() => handlePreferenceChange('weekStartsOn', 'Sunday')}>Sun</button>
                  <button className={`set-seg ${preferences.weekStartsOn === 'Monday' ? 'on' : 'off'}`} onClick={() => handlePreferenceChange('weekStartsOn', 'Monday')}>Mon</button>
                </div>
              </Row>
              <Row label="Default View">
                <select value={preferences.defaultView} onChange={(e) => handlePreferenceChange('defaultView', e.target.value)} className="set-select">
                  <option value="day">Day</option><option value="week">Week</option><option value="grid">Grid</option>
                </select>
              </Row>
              <Row label="Auto Sync"><Toggle checked={preferences.autoSync} onChange={(c) => handlePreferenceChange('autoSync', c)} /></Row>
            </div>
          </Section>

          {/* Privacy & Data */}
          <Section icon={Shield} title="Privacy & Data">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Row label="Save Search History"><Toggle checked={privacy.saveSearchHistory} onChange={(c) => handlePrivacyChange('saveSearchHistory', c)} /></Row>
              <Row label="Remember Filters"><Toggle checked={privacy.rememberFilters} onChange={(c) => handlePrivacyChange('rememberFilters', c)} /></Row>
            </div>
          </Section>

          {/* Data Management */}
          <Section icon={Download} title="Data Management">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <button onClick={handleExportSettings} className="set-btn"><Download className="w-4 h-4" /> Export</button>
              <button onClick={() => fileInputRef.current?.click()} className="set-btn"><Upload className="w-4 h-4" /> Import</button>
              <input ref={fileInputRef} type="file" accept=".json" onChange={handleImportSettings} className="hidden" />
              <button onClick={() => setShowResetConfirm(true)} className="set-btn" style={{ color: '#F59E0B', borderColor: 'rgba(245,158,11,.25)', background: 'rgba(245,158,11,.06)' }}><RotateCcw className="w-4 h-4" /> Reset</button>
              <button onClick={() => setShowClearDataConfirm(true)} className="set-btn" style={{ color: '#EF4444', borderColor: 'rgba(239,68,68,.25)', background: 'rgba(239,68,68,.06)' }}><AlertTriangle className="w-4 h-4" /> Clear All</button>
            </div>
          </Section>

          {/* Help & About */}
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 16px' }}>
            <div className="set-row" style={{ marginBottom: 6 }}>
              <span style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.1em', color: T.text25 }}>VERSION</span>
              <span style={{ fontFamily: T.mono, fontSize: 11, color: T.text60 }}>2.0.0 · 2025.01</span>
            </div>
            <p style={{ fontSize: 11, color: T.text40, lineHeight: 1.6, fontWeight: 300, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${T.border}` }}>
              University Timetable App — your smart schedule companion.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: 16, borderTop: `1px solid ${T.border}`, flexShrink: 0 }}>
          <button onClick={onClose} className="set-btn" style={{ width: '100%', padding: 13, color: T.teal, borderColor: T.tealBorder, background: T.tealBg }}>
            Done
          </button>
        </div>
      </div>

      {/* Reset confirm */}
      {showResetConfirm && (
        <ConfirmDialog
          icon={RotateCcw} accent="#F59E0B" title="Reset Settings?"
          body="This will restore all settings to their default values."
          confirmLabel="Reset" onCancel={() => setShowResetConfirm(false)} onConfirm={handleResetSettings}
        />
      )}

      {/* Clear data confirm */}
      {showClearDataConfirm && (
        <ConfirmDialog
          icon={AlertTriangle} accent="#EF4444" title="Clear All Data?"
          body="This will delete all your data including favorites, search history, and settings. This cannot be undone."
          confirmLabel="Clear All" onCancel={() => setShowClearDataConfirm(false)} onConfirm={handleClearAllData}
        />
      )}

      {/* Edit profile */}
      {showEditProfile && <EditProfileModal isOpen={showEditProfile} onClose={() => setShowEditProfile(false)} />}
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────────
function Section({ icon: Icon, title, action, children }: { icon: any; title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon className="w-4 h-4" color={T.teal} />
          <h3 style={{ fontSize: 13, fontWeight: 500, color: T.text }}>{title}</h3>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Row({ label, icon: Icon, children }: { label: string; icon?: any; children: React.ReactNode }) {
  return (
    <div className="set-row">
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.text60 }}>
        {Icon && <Icon className="w-4 h-4" color={T.text40} />}{label}
      </span>
      {children}
    </div>
  );
}

function Toggle({ checked, onChange, disabled = false }: { checked: boolean; onChange: (c: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      style={{
        position: 'relative', width: 44, height: 24, borderRadius: 12, flexShrink: 0, cursor: disabled ? 'not-allowed' : 'pointer',
        border: 'none', transition: '.2s', opacity: disabled ? 0.5 : 1,
        background: checked ? 'rgba(20,184,166,.3)' : 'rgba(255,255,255,.1)',
      }}
    >
      <span style={{ position: 'absolute', top: 3, left: checked ? 23 : 3, width: 18, height: 18, borderRadius: '50%', transition: '.2s', background: checked ? T.teal : 'rgba(255,255,255,.6)' }} />
    </button>
  );
}

function ConfirmDialog({ icon: Icon, accent, title, body, confirmLabel, onCancel, onConfirm }: {
  icon: any; accent: string; title: string; body: string; confirmLabel: string; onCancel: () => void; onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(4px)' }} onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: 16, padding: 22, width: '100%', maxWidth: 360, boxShadow: '0 24px 70px rgba(0,0,0,.6)', fontFamily: T.sans, color: T.text }}>
        <div style={{ width: 48, height: 48, borderRadius: 12, margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${accent}1a`, border: `1px solid ${accent}40`, color: accent }}>
          <Icon className="w-6 h-6" />
        </div>
        <h3 style={{ fontFamily: T.serif, fontSize: 20, fontWeight: 400, textAlign: 'center', marginBottom: 8 }}>{title}</h3>
        <p style={{ fontSize: 12, lineHeight: 1.6, color: T.text40, textAlign: 'center', marginBottom: 20, fontWeight: 300 }}>{body}</p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onCancel} className="set-btn" style={{ flex: 1 }}>Cancel</button>
          <button onClick={onConfirm} className="set-btn" style={{ flex: 1, color: accent, borderColor: `${accent}55`, background: `${accent}14` }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}
